import { Link } from "@inertiajs/react";
import { CirclePlusIcon, EllipsisIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Merchant, PropsWithUser } from "@/types";
import { PageTitle } from "@/components/page-title";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TableCellLink } from "@/components/table-cell-link";

type Props = {
  merchants: Merchant[];
};

function MerchantIndex({ merchants }: PropsWithUser<Props>) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <PageTitle>Merchants</PageTitle>
        <Button asChild>
          <Link href="/merchants/new">
            <CirclePlusIcon />
            Add merchant
          </Link>
        </Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {merchants.map((merchant) => {
              const href = `/merchants/${merchant.id}`;

              return (
                <TableRow
                  key={merchant.id}
                  className="has-[[data-row-link]:focus-visible]:outline-ring/50 has-[[data-row-link]:focus-visible]:outline-2 has-[[data-row-link]:focus-visible]:-outline-offset-2"
                >
                  <TableCell className="font-medium">
                    <TableCellLink href={href} />
                    {merchant.name}
                  </TableCell>
                  <TableCell>
                    <MerchantActions merchant={merchant} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function MerchantActions({ merchant }: { merchant: Merchant }) {
  return (
    <AlertDialog>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-8">
            <EllipsisIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          <AlertDialogTrigger asChild>
            <DropdownMenuItem>Delete</DropdownMenuItem>
          </AlertDialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete merchant</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete{" "}
            <span className="font-semibold">{merchant.name}</span> merchant.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" asChild>
            <Link
              href={`/merchants/${merchant.id}`}
              method="delete"
              preserveScroll
            >
              Delete
            </Link>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default MerchantIndex;
